import React, { useState } from "react";

const POINTS_OPTIONS = [
  { value: 0, label: "0" },
  { value: 1, label: "15" },
  { value: 2, label: "30" },
  { value: 3, label: "40" },
  { value: 4, label: "AD" },
];

const JEU_MAX = 7;

function ScoreInitModal({ onConfirm, onClose, formatMatch = "BEST_OF_3", premierServeur = "J1" }) {
  const maxSets = formatMatch === "BEST_OF_5" ? 5 : 3;
  const setsToWin = formatMatch === "BEST_OF_5" ? 3 : 2;

  const [sets, setSets] = useState([]);
  const [jeuJ1, setJeuJ1] = useState(0);
  const [jeuJ2, setJeuJ2] = useState(0);
  const [ptJ1, setPtJ1] = useState(0);
  const [ptJ2, setPtJ2] = useState(0);
  const [server, setServer] = useState(premierServeur);
  const [tieBreakActive, setTieBreakActive] = useState(false);
  const [tbJ1, setTbJ1] = useState(0);
  const [tbJ2, setTbJ2] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [showValidation, setShowValidation] = useState(false);

  const addSet = () => {
    if (sets.length >= maxSets - 1) return;
    setSets([...sets, { j1: 0, j2: 0, hasTb: false, tb1: 0, tb2: 0 }]);
  };

  const removeSet = () => {
    if (sets.length === 0) return;
    setSets(sets.slice(0, -1));
  };



  const j1SetsWon = sets.filter(s => s.j1 > s.j2).length;
  const j2SetsWon = sets.filter(s => s.j2 > s.j1).length;
  const matchAlreadyOver = j1SetsWon >= setsToWin || j2SetsWon >= setsToWin;

  const handleConfirm = () => {
    setShowValidation(true);
    if (matchAlreadyOver) {
      setErrorMsg("Le score saisi indique que le match est déjà terminé.");
      return;
    }
    if (invalidSets.length > 0) {
      setErrorMsg("Certains scores de sets sont invalides — ex: 6-4, 7-5, 7-6.");
      return;
    }
    const invalidTbScores = sets.filter(s => {
      if (!s.hasTb || (s.tb1 === 0 && s.tb2 === 0)) return false;
      const maxTb = Math.max(s.tb1, s.tb2);
      const diff = Math.abs(s.tb1 - s.tb2);
      return !(maxTb >= 7 && diff >= 2);
    });
    if (invalidTbScores.length > 0) {
      setErrorMsg("Score(s) de tie-break invalide(s) — ex: 7-5, 7-3, 10-8.");
      return;
    }

    setErrorMsg('');
    onConfirm({
      sets: sets.map(s => [s.j1, s.j2]),
      setsTb: sets.map(s => s.hasTb ? [s.tb1, s.tb2] : null),
      jeuScore: [jeuJ1, jeuJ2],
      pointsJeu: tieBreakActive ? [tbJ1, tbJ2] : [ptJ1, ptJ2],
      server,
      tieBreakActive,
      superTieBreakActive: false,
      tieBreakPoints: tieBreakActive ? [tbJ1, tbJ2] : [0, 0],
    });
  };

  const activeBtn = (active) => ({
    padding: "6px 10px",
    borderRadius: "6px",
    border: `2px solid ${active ? "#1976d2" : "#ddd"}`,
    backgroundColor: active ? "#1976d2" : "white",
    color: active ? "white" : "#333",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "14px",
    minWidth: "40px",
  });

  const btnCounter = {
    padding: "4px 10px",
    borderRadius: "6px",
    border: "1px solid #bbb",
    backgroundColor: "white",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "bold",
    color: "#333",
  };

  const numStyle = {
    fontSize: "20px",
    fontWeight: "700",
    minWidth: "28px",
    textAlign: "center",
  };


  // ── Règles de cohérence ──────────────────────────────────────

  // 1. AD disponible seulement si l'autre joueur est à 40 (index 3)
  const adDisabledJ1 = ptJ2 !== 3; // J1 ne peut pas avoir AD si J2 n'est pas à 40
  const adDisabledJ2 = ptJ1 !== 3; // J2 ne peut pas avoir AD si J1 n'est pas à 40

  // 2. Validation score set terminé
  const isValidSetScore = (j1, j2) => {
    if (j1 === 0 && j2 === 0) return true; // pas encore saisi
    if (j1 === 7 && j2 === 6) return true;
    if (j1 === 6 && j2 === 7) return true;
    if (j1 === 7 && j2 === 5) return true;
    if (j1 === 5 && j2 === 7) return true;
    if (j1 === 6 && j2 <= 4) return true;
    if (j2 === 6 && j1 <= 4) return true;
    return false;
  };

  // Max J2 quand J1 = 7 : seulement 5 ou 6
  // 4. Validation tie-break


  // Validation globale des sets
  const invalidSets = sets.filter(s => !isValidSetScore(s.j1, s.j2));

  // Mise à jour updateSet avec contraintes max
  const updateSetConstrained = (index, player, delta) => {
    setShowValidation(false);
    const updated = sets.map((s, i) => {
      if (i !== index) return s;
      let newVal = Math.min(7, Math.max(0, s[player] + delta));
      if (player === 'j1') {
        if (newVal === 7 && s.j2 < 5) return { ...s, j1: newVal, j2: 5 };
        if (s.j2 === 7 && newVal > 6) return s;
      }
      if (player === 'j2') {
        if (newVal === 7 && s.j1 < 5) return { ...s, j2: newVal, j1: 5 };
        if (s.j1 === 7 && newVal > 6) return s;
      }
      return { ...s, [player]: newVal };
    });
    setSets(updated);
  };

  const toggleSetTb = (index, checked) => {
    setSets(sets.map((s, i) => i === index ? { ...s, hasTb: checked, tb1: 0, tb2: 0 } : s));
  };

  const updateSetTb = (index, player, delta) => {
    setShowValidation(false);
    setSets(sets.map((s, i) =>
      i === index ? { ...s, [player]: Math.max(0, s[player] + delta) } : s
    ));
  };

  // Résumé textuel
  const setsStr = sets.map(s => `${s.j1}-${s.j2}`).join(", ");
  const currentSetStr = `${jeuJ1}-${jeuJ2}`;
  const ptStr = tieBreakActive
    ? `TB: ${tbJ1}-${tbJ2}`
    : `${POINTS_OPTIONS[ptJ1]?.label}-${POINTS_OPTIONS[ptJ2]?.label}`;

  return (
    <div
      style={{
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: "rgba(0,0,0,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "16px",
          padding: "20px",
          maxWidth: "400px",
          width: "92%",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
        }}
        onClick={e => e.stopPropagation()}
      >
        <h3 style={{ margin: "0 0 16px 0", fontSize: "17px", color: "#333" }}>
          ⚡ Saisir le score en cours
        </h3>

        {/* Sets terminés */}
        <div style={{ marginBottom: "16px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <span style={{ fontWeight: "600", fontSize: "14px" }}>Sets terminés — score final de chaque set</span>
            <div style={{ display: "flex", gap: "6px" }}>
              <button type="button" onClick={removeSet} disabled={sets.length === 0} style={activeBtn(false)}>−</button>
              <button type="button" onClick={addSet} disabled={sets.length >= maxSets - 1} style={activeBtn(false)}>+ Set</button>
            </div>
          </div>

          {sets.length === 0 && (
            <div style={{ color: "#999", fontSize: "13px", fontStyle: "italic", padding: "4px 0" }}>
              Aucun set terminé — match au 1er set
            </div>
          )}
          {sets.length > 0 && (
            <div style={{ color: "#888", fontSize: "11px", marginBottom: "6px" }}>
              Utilisez + et − pour saisir le score final de chaque set (ex: 6-4)
            </div>
          )}

          {sets.map((s, i) => (
            <div key={i} style={{
              display: "flex", flexDirection: "column",
              padding: "8px 10px",
              backgroundColor: "#e3eaf5",
              borderRadius: "8px",
              marginBottom: "6px",
              gap: "6px"
            }}>
              {/* Ligne scores */}
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ color: "#333", fontSize: "13px", minWidth: "55px", fontWeight: "600" }}>Set {i + 1} :</span>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <button type="button" onClick={() => updateSetConstrained(i, 'j1', -1)} disabled={s.j1 === 0} style={btnCounter}>−</button>
                  <span style={numStyle}>{s.j1}</span>
                  <button type="button" onClick={() => updateSetConstrained(i, 'j1', 1)} style={btnCounter}>+</button>
                </div>
                <span style={{ fontWeight: "bold" }}>-</span>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <button type="button" onClick={() => updateSetConstrained(i, 'j2', -1)} disabled={s.j2 === 0} style={btnCounter}>−</button>
                  <span style={numStyle}>{s.j2}</span>
                  <button type="button" onClick={() => updateSetConstrained(i, 'j2', 1)} style={btnCounter}>+</button>
                </div>
              </div>
              {/* TB du set terminé - en dessous à gauche */}
              {((s.j1 === 7 && s.j2 === 6) || (s.j1 === 6 && s.j2 === 7)) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#555', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={s.hasTb || false}
                      onChange={e => toggleSetTb(i, e.target.checked)}
                      style={{ cursor: 'pointer' }}
                    />
                    Score du tie-break
                  </label>
                  {s.hasTb && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '22px' }}>
                      <button type="button" onClick={() => updateSetTb(i, 'tb1', -1)} disabled={s.tb1 === 0} style={btnCounter}>−</button>
                      <span style={{ ...numStyle, fontSize: '14px', minWidth: '20px' }}>{s.tb1}</span>
                      <button type="button" onClick={() => updateSetTb(i, 'tb1', 1)} style={btnCounter}>+</button>
                      <span style={{ fontWeight: 'bold' }}>-</span>
                      <button type="button" onClick={() => updateSetTb(i, 'tb2', -1)} disabled={s.tb2 === 0} style={btnCounter}>−</button>
                      <span style={{ ...numStyle, fontSize: '14px', minWidth: '20px' }}>{s.tb2}</span>
                      <button type="button" onClick={() => updateSetTb(i, 'tb2', 1)} style={btnCounter}>+</button>
                      {showValidation && (s.tb1 > 0 || s.tb2 > 0) ? (
                        (() => {
                          const maxTb = Math.max(s.tb1, s.tb2);
                          const diff = Math.abs(s.tb1 - s.tb2);
                          const valid = maxTb >= 7 && diff >= 2;
                          return !valid ? (
                            <span style={{ fontSize: '10px', color: '#e65100', marginLeft: '4px' }}>⚠️ invalide</span>
                          ) : null;
                        })()
                      ) : null}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Jeux set en cours */}
        <div style={{ marginBottom: "16px" }}>
          <div style={{ fontWeight: "600", fontSize: "14px", marginBottom: "8px" }}>
            Jeux — Set {sets.length + 1} en cours
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <button type="button" onClick={() => setJeuJ1(Math.max(0, jeuJ1 - 1))} disabled={jeuJ1 === 0} style={btnCounter}>−</button>
              <span style={numStyle}>{jeuJ1}</span>
              <button type="button" onClick={() => setJeuJ1(Math.min(JEU_MAX, jeuJ1 + 1))} style={btnCounter}>+</button>
            </div>
            <span style={{ fontWeight: "bold", fontSize: "18px" }}>-</span>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <button type="button" onClick={() => setJeuJ2(Math.max(0, jeuJ2 - 1))} disabled={jeuJ2 === 0} style={btnCounter}>−</button>
              <span style={numStyle}>{jeuJ2}</span>
              <button type="button" onClick={() => setJeuJ2(Math.min(JEU_MAX, jeuJ2 + 1))} style={btnCounter}>+</button>
            </div>
          </div>
        </div>

        {/* Tie-break toggle */}
        <div style={{ marginBottom: "12px" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={tieBreakActive}
              onChange={e => setTieBreakActive(e.target.checked)}
              style={{ width: "16px", height: "16px", cursor: "pointer" }}
            />
            <span style={{ fontSize: "14px", fontWeight: "600" }}>Tie-break en cours</span>
          </label>
        </div>

        {/* Points jeu / tie-break */}
        <div style={{ marginBottom: "16px" }}>
          <div style={{ fontWeight: "600", fontSize: "14px", marginBottom: "8px" }}>
            {tieBreakActive ? "Points tie-break" : "Points dans le jeu"}
          </div>
          {tieBreakActive ? (
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <button type="button" onClick={() => setTbJ1(Math.max(0, tbJ1 - 1))} disabled={tbJ1 === 0} style={btnCounter}>−</button>
                <span style={numStyle}>{tbJ1}</span>
                <button type="button" onClick={() => setTbJ1(tbJ1 + 1)} style={btnCounter}>+</button>
              </div>
              <span style={{ fontWeight: "bold", fontSize: "18px" }}>-</span>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <button type="button" onClick={() => setTbJ2(Math.max(0, tbJ2 - 1))} disabled={tbJ2 === 0} style={btnCounter}>−</button>
                <span style={numStyle}>{tbJ2}</span>
                <button type="button" onClick={() => setTbJ2(tbJ2 + 1)} style={btnCounter}>+</button>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
              <div>
                <div style={{ fontSize: "11px", color: "#666", marginBottom: "4px" }}>J1</div>
                <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                  {POINTS_OPTIONS.map(opt => {
                    const isDisabled = opt.value === 4 && adDisabledJ1;
                    return (
                      <button
                        type="button"
                        key={opt.value}
                        onClick={() => !isDisabled && setPtJ1(opt.value)}
                        style={{
                          ...activeBtn(ptJ1 === opt.value),
                          opacity: isDisabled ? 0.3 : 1,
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <span style={{ fontWeight: "bold", fontSize: "18px", paddingTop: "20px" }}>-</span>
              <div>
                <div style={{ fontSize: "11px", color: "#666", marginBottom: "4px" }}>J2</div>
                <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                  {POINTS_OPTIONS.map(opt => {
                    const isDisabled = opt.value === 4 && adDisabledJ2;
                    return (
                      <button
                        type="button"
                        key={opt.value}
                        onClick={() => !isDisabled && setPtJ2(opt.value)}
                        style={{
                          ...activeBtn(ptJ2 === opt.value),
                          opacity: isDisabled ? 0.3 : 1,
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Serveur */}
        <div style={{ marginBottom: "16px" }}>
          <div style={{ fontWeight: "600", fontSize: "14px", marginBottom: "8px" }}>Serveur actuel</div>
          <div style={{ display: "flex", gap: "10px" }}>
            <button type="button" onClick={() => setServer("J1")} style={activeBtn(server === "J1")}>J1</button>
            <button type="button" onClick={() => setServer("J2")} style={activeBtn(server === "J2")}>J2</button>
          </div>
        </div>

        {/* Résumé */}
        {showValidation && (() => {
          const invalidTb = sets.filter(s => {
            if (!s.hasTb || (s.tb1 === 0 && s.tb2 === 0)) return false;
            const maxTb = Math.max(s.tb1, s.tb2);
            const diff = Math.abs(s.tb1 - s.tb2);
            return !(maxTb >= 7 && diff >= 2);
          });
          return invalidTb.length > 0 ? (
            <div style={{ padding: "6px 12px", backgroundColor: "#fff3e0", borderRadius: "8px", marginBottom: "8px", fontSize: "12px", color: "#e65100" }}>
              ⚠️ Score(s) de tie-break invalide(s) — ex: 7-5, 7-3, 10-8
            </div>
          ) : null;
        })()}
        {showValidation && invalidSets.length > 0 && (
          <div style={{ padding: "6px 12px", backgroundColor: "#fff3e0", borderRadius: "8px", marginBottom: "8px", fontSize: "12px", color: "#e65100" }}>
            ⚠️ Score(s) de set invalide(s) — ex: 6-4, 7-5, 7-6
          </div>
        )}

        <div style={{
          padding: "8px 12px",
          backgroundColor: matchAlreadyOver ? "#ffebee" : "#e8f5e9",
          borderRadius: "8px",
          marginBottom: "16px",
          fontSize: "12px",
          color: matchAlreadyOver ? "#c62828" : "#2e7d32",
        }}>
          {matchAlreadyOver
            ? "⚠️ Score invalide : le match est déjà terminé"
            : `${setsStr ? setsStr + " / " : ""}${currentSetStr} (${ptStr}) — Serveur : ${server}`
          }
        </div>

        {/* Message d'erreur inline */}
        {errorMsg && (
          <div style={{
            padding: "8px 12px",
            backgroundColor: "#ffebee",
            borderRadius: "8px",
            marginBottom: "12px",
            fontSize: "13px",
            color: "#c62828",
            fontWeight: "500",
          }}>
            ⚠️ {errorMsg}
          </div>
        )}

        {/* Boutons */}
        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
          <button type="button" onClick={onClose} style={{
            padding: "9px 18px", borderRadius: "8px",
            border: "1px solid #ddd", backgroundColor: "white",
            color: "#333", cursor: "pointer", fontSize: "14px", fontWeight: "600"
          }}>
            Annuler
          </button>
          <button type="button" onClick={handleConfirm} disabled={matchAlreadyOver} style={{
            padding: "9px 18px", borderRadius: "8px", border: "none",
            backgroundColor: matchAlreadyOver ? "#ccc" : "#1976d2",
            color: "white", cursor: matchAlreadyOver ? "default" : "pointer",
            fontSize: "14px", fontWeight: "600"
          }}>
            Confirmer
          </button>
        </div>
      </div>
    </div>
  );
}

export default ScoreInitModal;